/**
 * Список курсов
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from './AdminShell';
import { getCourses, deleteCourse, publishCourse, unpublishCourse, CourseListItem } from '../../lib/admin-api';

export default function AdminCourses() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState<CourseListItem[]>([]);

  const loadCourses = async () => {
    try {
      const data = await getCourses();
      setCourses(data);
    } catch (err) {
      console.error('Failed to load courses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Удалить курс "${name}"?`)) return;
    try {
      await deleteCourse(id);
      loadCourses();
    } catch (err) {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Ошибка удаления', type: 'error' } }));
    }
  };

  const handleTogglePublish = async (course: CourseListItem) => {
    try {
      if (course.isPublished) {
        await unpublishCourse(course.id);
      } else {
        await publishCourse(course.id);
      }
      loadCourses();
    } catch (err) {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Ошибка', type: 'error' } }));
    }
  };

  if (loading) {
    return (
      <AdminShell title="Курсы" onBack={() => navigate('/admin')}>
        <div className="flex min-h-[50vh] items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#0F766E] border-t-transparent rounded-full animate-spin" />
      </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Курсы" onBack={() => navigate('/admin')} actions={
      <button onClick={() => navigate('/admin/courses/new')} className="rounded-lg bg-[#0F766E] px-3 py-2 text-sm text-white hover:bg-[#0B5F59]">+ Новый курс</button>
    }>
      <main>
        <label className="mb-4 flex flex-wrap items-center gap-3 text-sm text-[#626B73]">
          Состояние
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="rounded-lg px-3 py-2">
            <option value="all">Все курсы</option>
            <option value="published">Опубликованные</option>
            <option value="draft">Черновики</option>
          </select>
        </label>
        <table className="block w-full rounded-xl border border-[#E1E5E3] bg-white text-left text-sm lg:table">
          <caption className="sr-only">Курсы и действия управления</caption>
          <thead className="hidden border-b border-[#E1E5E3] bg-[#F7F7F4] text-[#626B73] lg:table-header-group">
            <tr><th className="p-4">Курс</th><th className="p-4">Состояние</th><th className="p-4">Доступ</th><th className="p-4">Модули / уроки</th><th className="p-4">Действия</th></tr>
          </thead>
          <tbody className="block divide-y divide-[#E1E5E3] lg:table-row-group">
            {courses.filter(course => statusFilter === 'all' || course.isPublished === (statusFilter === 'published')).map(course => {
              const accessType = (course as CourseListItem & { access?: { type?: string } }).access?.type;
              const accessLabel = ({ free: 'Бесплатный', freemium: 'Частично платный', premium: 'Платный', private: 'Личный' } as Record<string, string>)[accessType || ''] || 'Не указан';
              return <tr key={course.id} className="block p-4 lg:table-row lg:p-0">
                <td className="block pb-3 lg:table-cell lg:p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{course.icon}</span>
                    <div className="min-w-0"><h2 className="font-semibold">{course.name}</h2><p className="mt-1 text-xs text-[#626B73] lg:max-w-sm">{course.description}</p></div>
                  </div>
                </td>
                <td className="block py-1 lg:table-cell lg:p-4"><span className={`inline-block whitespace-nowrap rounded-full px-2 py-1 text-xs ${course.isPublished ? 'bg-[#E5F2EF] text-[#0F766E]' : 'bg-[#F7F7F4] text-[#626B73]'}`}>{course.isPublished ? 'Опубликован' : 'Черновик'}</span></td>
                <td className="block py-1 lg:table-cell lg:p-4"><span className="text-[#626B73] lg:hidden">Доступ: </span>{accessLabel}</td>
                <td className="block py-1 text-[#626B73] lg:table-cell lg:p-4">{course.modulesCount} мод. / {course.lessonsCount} ур.</td>
                <td className="block pt-3 lg:table-cell lg:p-4">
                  <div className="flex flex-wrap gap-2 lg:max-w-xs">
                    <button onClick={() => navigate(`/admin/courses/${course.id}`)} className="rounded-lg bg-[#0F766E] px-3 py-2 text-white hover:bg-[#0B5F59]">Редактировать</button>
                    <a href={`/collections/${course.id}`} target="_blank" rel="noreferrer" className="rounded-lg bg-[#E5F2EF] px-3 py-2 text-[#0F766E] hover:text-[#0B5F59]">Открыть на сайте ↗</a>
                    <button onClick={() => handleTogglePublish(course)} className="rounded-lg border border-[#E1E5E3] px-3 py-2 text-[#0F766E] hover:bg-[#E5F2EF]">{course.isPublished ? 'Снять с публикации' : 'Опубликовать'}</button>
                    <button onClick={() => handleDelete(course.id, course.name)} className="rounded-lg bg-red-50 px-3 py-2 text-[#DC2626]">Удалить</button>
                  </div>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
        {courses.length > 0 && !courses.some(course => statusFilter === 'all' || course.isPublished === (statusFilter === 'published')) && <p className="py-12 text-center text-[#626B73]">Курсов с таким состоянием нет</p>}
        {courses.length === 0 && <div className="py-12 text-center text-[#626B73]">
          <p>Курсов пока нет</p>
          <button onClick={() => navigate('/admin/courses/new')} className="mt-4 rounded-lg bg-[#0F766E] px-4 py-2 text-white hover:bg-[#0B5F59]">Создать первый курс</button>
        </div>}
      </main>
    </AdminShell>
  );
}
